import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Switch } from "@/shared/components/ui/switch";
import { RotateCw } from "lucide-react";
import { useEffect, useMemo } from "react";
import { VersioningScheduleSourceDialog } from "../components/VersioningScheduleSourceDialog";
import { useMediaVersionUpdateAlertSettings } from "../hooks/useMediaVersionUpdateAlertSettings";
import { useVersioningScheduleColumns } from "../hooks/useVersioningScheduleColumns";

export function InfoMediaVersionUpdateAlertSettingsPage() {
  const vm = useMediaVersionUpdateAlertSettings();
  const { load } = vm;
  const { canWriteCurrentPage } = useCurrentPageAccess();
  useEffect(() => {
    load();
  }, [load]);

  const columns = useVersioningScheduleColumns();

  const tableData = useMemo(() => vm.rows, [vm.rows]);

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="매체 버전 업데이트 알림 설정"
        description="매체 API 버전 크롤링 정보를 확인하고 추가, 수정 및 삭제합니다."
      />

      <Card className="p-4 space-y-3">
        <div className="text-base font-semibold">버저닝 일정 알림 동작 ON/OFF</div>
        <div className="flex items-center gap-3">
          <Switch
            checked={vm.configs.enable}
            onCheckedChange={(checked) => vm.toggleConfig("enable", checked)}
            disabled={!canWriteCurrentPage}
          />
          <div className="font-bold">{vm.configs.enable ? "ON" : "OFF"}</div>
        </div>
      </Card>

      <DataTable
        columns={columns}
        data={tableData}
        totalCount={vm.rows.length}
        page={1}
        pageSize={50}
        enableRowSelection
        rowSelectionTrigger="checkbox"
        getRowId={(r) => r.id}
        onRowSelectionChange={(selected) => vm.setSelectedIds(selected.map((r) => r.id))}
        onRowClick={canWriteCurrentPage ? (row) => vm.openEdit(row) : undefined}
        toolbar={
          <DataTableToolbar
            filters={
              <div className="text-sm text-muted-foreground">
                {vm.summary.selected > 0 && (
                  <>
                    {" · "}선택{" "}
                    <span className="font-medium text-foreground">{vm.summary.selected}</span>개
                  </>
                )}
              </div>
            }
            actions={
              <>
                <Button variant="outline" onClick={vm.load}>
                  <RotateCw className="mr-2 h-4 w-4" />
                  새로고침
                </Button>
                <Button onClick={vm.openAdd} disabled={!canWriteCurrentPage}>
                  추가
                </Button>
                <Button
                  variant="destructive"
                  onClick={vm.deleteSelected}
                  disabled={!canWriteCurrentPage || vm.summary.selected === 0}
                >
                  삭제
                </Button>
              </>
            }
          />
        }
      />

      <VersioningScheduleSourceDialog
        open={vm.modalOpen}
        mode={vm.modalMode}
        value={vm.form}
        onChange={vm.setForm}
        onCancel={vm.closeModal}
        onSubmit={vm.submit}
      />
    </div>
  );
}
